// src/pages/admin/SeedTeamDemo.jsx
// Admin utility: seed a believable Spanish-speaking club into the teams you
// own/coach — roster, an open season (with payment plans + financial aid),
// registrations (paid / on-plan / fee-waived / pending), plus a chat + next
// session — so demo walkthroughs and App Store / Play screenshots have real,
// on-brand, bilingual data. Everything is flagged `demo: true` and the Clear
// button removes it cleanly, so it never mixes with real club data.

import { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';

/* ─── Demo roster: realistic Spanish-speaking families ───────────────────
   Emails use example.com (reserved for examples) so nothing can ever be
   delivered. Phones are fake 555 numbers. language 'es' on guardians drives
   the bilingual invite email + landing page. */
const DEMO_PLAYERS = [
  { first: 'Mateo',     last: 'Rivera',    jersey: '10', status: 'active',   pos: 'Forward',      g: { name: 'Lucía Rivera',    rel: 'Mother', lang: 'es' } },
  { first: 'Sofía',     last: 'Hernández', jersey: '7',  status: 'active',   pos: 'Midfielder',   g: { name: 'Carlos Hernández', rel: 'Father', lang: 'es' } },
  { first: 'Diego',     last: 'Morales',   jersey: '4',  status: 'active',   pos: 'Defender',     g: { name: 'Yolanda Morales',  rel: 'Mother', lang: 'es' } },
  { first: 'Valentina', last: 'Cruz',      jersey: '11', status: 'active',   pos: 'Forward',      g: { name: 'Ana Cruz',         rel: 'Mother', lang: 'es' } },
  { first: 'Santiago',  last: 'Flores',    jersey: '9',  status: 'active',   pos: 'Forward',      g: { name: 'José Flores',      rel: 'Father', lang: 'es' } },
  { first: 'Camila',    last: 'Ortiz',     jersey: '3',  status: 'active',   pos: 'Defender',     g: { name: 'María Ortiz',      rel: 'Mother', lang: 'es' } },
  { first: 'Lucas',     last: 'Ramírez',   jersey: '8',  status: 'active',   pos: 'Midfielder',   g: { name: 'Pedro Ramírez',    rel: 'Father', lang: 'es' } },
  { first: 'Isabella',  last: 'Torres',    jersey: '6',  status: 'active',   pos: 'Goalkeeper',   g: { name: 'Rosa Torres',      rel: 'Mother', lang: 'es' } },
  { first: 'Emiliano',  last: 'Vargas',    jersey: '5',  status: 'prospect', pos: 'Midfielder',   g: { name: 'Juan Vargas',      rel: 'Father', lang: 'es' } },
  { first: 'Daniela',   last: 'Castillo',  jersey: '12', status: 'prospect', pos: 'Defender',     g: { name: 'Gloria Castillo',  rel: 'Mother', lang: 'en' } },
];

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');
const demoEmail = (g, p) => `${slug(g.name).slice(0, 14) || 'familia'}.${slug(p.last)}@example.com`;
const demoPhone = (i) => `+1626555${String(1000 + i).slice(-4)}`;

export default function SeedTeamDemo() {
  const { firebaseUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(''); // '' until loaded; 'ALL' or a team id

  const uid = firebaseUser?.uid;
  const addLog = (line) => setLog((prev) => [...prev, line]);

  // Fetch the teams this account owns or coaches.
  const getMyTeams = async () => {
    const teamsCol = collection(db, 'teams');
    const [ownedSnap, coachedSnap] = await Promise.all([
      getDocs(query(teamsCol, where('ownerId', '==', uid))),
      getDocs(query(teamsCol, where('coachId', '==', uid))),
    ]);
    const teamMap = new Map();
    [...ownedSnap.docs, ...coachedSnap.docs].forEach((d) => teamMap.set(d.id, { id: d.id, ...d.data() }));
    return Array.from(teamMap.values());
  };

  // Load the team list for the picker once we know the user.
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getMyTeams();
        if (cancelled) return;
        setTeams(list);
        // Default the picker to the single team if there's only one.
        setSelectedTeamId((cur) => cur || (list.length === 1 ? list[0].id : ''));
      } catch (e) {
        console.error('[SeedTeamDemo] team load failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // Remove every demo-flagged doc. Top-level collections clear by `demo==true`;
  // players live in a per-team subcollection so we walk each team.
  const clearDemoDocs = async (teams) => {
    let deleted = 0;
    for (const col of ['chats', 'sessions', 'seasons', 'registrations', 'games']) {
      const snap = await getDocs(query(collection(db, col), where('demo', '==', true)));
      if (!snap.empty) {
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
        deleted += snap.size;
        addLog(`🧹 Cleared ${snap.size} demo doc(s) from /${col}`);
      }
    }
    const teamList = teams || (await getMyTeams());
    for (const team of teamList) {
      const snap = await getDocs(query(collection(db, 'teams', team.id, 'players'), where('demo', '==', true)));
      if (!snap.empty) {
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
        deleted += snap.size;
        addLog(`🧹 Cleared ${snap.size} demo player(s) from "${team.name}"`);
      }
    }
    return deleted;
  };

  const clearDemoData = async () => {
    if (!uid) { toast.error('Sign in first'); return; }
    setBusy(true); setLog([]);
    try {
      const n = await clearDemoDocs();
      addLog(n === 0 ? 'No demo docs to clear.' : `Done.`);
      toast.success(`Cleared ${n} demo doc(s).`);
    } catch (e) {
      console.error(e); toast.error(e.message || 'Clear failed'); addLog(`✗ ${e.message || 'Clear failed'}`);
    } finally { setBusy(false); }
  };

  // ── Roster ──────────────────────────────────────────────────────────
  const seedRoster = async (team) => {
    let n = 0;
    for (let i = 0; i < DEMO_PLAYERS.length; i++) {
      const p = DEMO_PLAYERS[i];
      const guardian = {
        name: p.g.name,
        email: demoEmail(p.g, p),
        phone: demoPhone(i),
        language: p.g.lang,
        relationship: p.g.rel,
      };
      await addDoc(collection(db, 'teams', team.id, 'players'), {
        firstName: p.first,
        lastName: p.last,
        jerseyNumber: p.jersey,
        position: p.pos,
        status: p.status,
        guardians: [guardian],
        // Legacy mirror fields (some screens read these)
        parentName: guardian.name,
        parentEmail: guardian.email,
        parentPhone: guardian.phone,
        language: guardian.language,
        photoURL: null,
        source: 'demo',
        demo: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      n++;
    }
    addLog(`✓ Seeded ${n} players (Spanish-speaking families) for "${team.name}"`);
  };

  // ── Season (open; payment plan + financial aid on) ──────────────────
  const seedSeason = async (team) => {
    const now = new Date();
    const d = (days) => Timestamp.fromDate(new Date(now.getTime() + days * 86400000));
    const ref = await addDoc(collection(db, 'seasons'), {
      teamId: team.id,
      coachId: uid,
      name: 'Primavera 2026',
      status: 'open',
      registrationOpen: d(-7),
      registrationClose: d(30),
      seasonStart: d(14),
      seasonEnd: d(100),
      maxPlayers: 16,
      registrationFee: 9000, // $90.00 in cents
      pricingMode: 'flat',
      paymentPlanEnabled: true,
      paymentPlanInstallments: 3,
      paymentPlanFrequency: 'monthly',
      paymentPlanChargeDay: 15,
      financialAidEnabled: true,
      processingFeeMode: 'pass_to_parent',
      whatsIncluded: [
        'Entrenamientos y sesiones de práctica',
        'Uniforme y equipo del jugador',
        'Inscripción a la liga y tarifas de árbitros',
        'Becas disponibles para familias que lo necesiten',
      ],
      requiredDocuments: ['birth_certificate', 'player_photo'],
      waivers: [{
        name: 'Exención de responsabilidad',
        text: 'Al inscribir a mi hijo/a reconozco que la participación en actividades de fútbol juvenil conlleva riesgos inherentes de lesión y autorizo su participación.',
      }],
      setupComplete: true,
      profileStatus: 'complete',
      demo: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    addLog(`✓ Season "Primavera 2026" seeded for "${team.name}"`);
    return ref.id;
  };

  // ── Registrations: paid / on payment plan / fee-waived / pending ────
  const seedRegistrations = async (team, seasonId) => {
    const fee = 9000;
    const reg = (p, status, pricing, extra = {}) => ({
      teamId: team.id,
      seasonId,
      playerName: `${p.first} ${p.last}`,
      parentName: p.g.name,
      parentEmail: demoEmail(p.g, p),
      parentPhone: '+16265550000',
      language: p.g.lang,
      status,
      amountDue: pricing.finalAmount,
      pricing,
      demo: true,
      createdAt: serverTimestamp(),
      ...extra,
    });
    const full = { baseAmount: fee, finalAmount: fee, totalDiscount: 0 };

    const docs = [
      // 3 paid in full
      reg(DEMO_PLAYERS[0], 'paid', full),
      reg(DEMO_PLAYERS[1], 'paid', full),
      reg(DEMO_PLAYERS[2], 'paid', full),
      // 1 on a 3-month payment plan: first installment captured
      reg(DEMO_PLAYERS[3], 'confirmed', full, {
        installmentsTotal: 3,
        paymentPlan: {
          schedule: [
            { amount: 3000, status: 'paid' },
            { amount: 3000, status: 'pending' },
            { amount: 3000, status: 'pending' },
          ],
        },
      }),
      // 1 fee-waived (financial aid 100%)
      reg(DEMO_PLAYERS[4], 'paid', {
        baseAmount: fee, finalAmount: 0, totalDiscount: fee, financialAidDiscount: fee,
      }),
      // 1 pending payment
      reg(DEMO_PLAYERS[5], 'pending_payment', full),
    ];
    for (const r of docs) await addDoc(collection(db, 'registrations'), r);
    addLog(`✓ Seeded ${docs.length} registrations (paid / plan / fee-waived / pending) for "${team.name}"`);
  };

  // ── Chat + next session (kept from the original tool, bilingual) ────
  const seedChatAndSession = async (team) => {
    const when = new Date();
    when.setDate(when.getDate() + 1);
    when.setHours(18, 0, 0, 0);
    await addDoc(collection(db, 'sessions'), {
      teamId: team.id,
      teamName: team.name || null,
      type: 'Practice',
      date: Timestamp.fromDate(when),
      location: team?.location?.name || team?.address || null,
      createdBy: uid,
      createdAt: serverTimestamp(),
      demo: true,
    });
    const nowTs = Timestamp.now();
    await addDoc(collection(db, 'chats'), {
      teams: [team.id],
      lastMessage: `¡Bienvenidos a ${team.name || 'tu equipo'}! 👋`,
      lastMessageAt: nowTs,
      lastUpdated: nowTs,
      readBy: { [team.id]: false },
      deletedBy: {}, archivedBy: {}, mutedUntilBy: {},
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      teamMeta: { [team.id]: { name: team.name || 'Team', logoUrl: team.logoUrl || null } },
      demo: true,
    });
    addLog(`✓ Chat + next session seeded for "${team.name}"`);
  };

  const seedForMyTeams = async () => {
    if (!uid) { toast.error('Sign in first'); return; }
    if (!selectedTeamId) { toast.error('Pick a team first'); return; }
    setBusy(true); setLog([]);
    try {
      const all = teams.length ? teams : await getMyTeams();
      if (!all.length) { addLog('No teams found for this account.'); toast.error('No teams found'); return; }

      const target = selectedTeamId === 'ALL'
        ? all
        : all.filter((t) => t.id === selectedTeamId);
      if (!target.length) { addLog('Selected team not found.'); toast.error('Team not found'); return; }
      addLog(`Seeding ${target.length} team(s): ${target.map((t) => t.name).join(', ')}`);

      // Idempotent: wipe prior demo data first (clears across all teams so a
      // re-seed never leaves stale demo data behind on another team).
      await clearDemoDocs(all);

      for (const team of target) {
        await seedRoster(team);
        const seasonId = await seedSeason(team);
        await seedRegistrations(team, seasonId);
        await seedChatAndSession(team);
      }
      toast.success('Demo club seeded. Refresh the app to see it.');
      addLog('✅ Done. Open Roster / Registrations / Financial Reports.');
    } catch (e) {
      console.error(e); toast.error(e.message || 'Seed failed'); addLog(`✗ ${e.message || 'Seed failed'}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl p-6 space-y-4">
      <h1 className="text-2xl font-bold">Seed Demo Club</h1>
      <p className="text-sm text-zinc-600 dark:text-zinc-400">
        Populates the chosen team with a believable Spanish-speaking club: a
        10-player roster (Spanish-speaking families, mix of active + prospects),
        an open season “Primavera 2026” with payment plans and financial aid,
        registrations (paid / on-plan / fee-waived / pending), plus a chat
        thread and next session. For demo walkthroughs and store screenshots.
        Everything is flagged <code>demo: true</code> — “Clear” removes it all
        (across every team) without touching real data. Seed is idempotent.
      </p>

      {/* Team picker — scope the seed to one team (recommended) or all. */}
      <div className="space-y-1">
        <label htmlFor="seed-team" className="block text-sm font-semibold text-zinc-700 dark:text-zinc-200">
          Seed into
        </label>
        <select
          id="seed-team"
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          disabled={busy || !teams.length}
          className="w-full sm:w-96 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 disabled:opacity-50"
        >
          <option value="" disabled>{teams.length ? 'Select a team…' : 'Loading teams…'}</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name || t.id}</option>
          ))}
          {teams.length > 1 && <option value="ALL">All my teams ({teams.length})</option>}
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={seedForMyTeams}
          disabled={busy || !uid || !selectedTeamId}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Working…' : 'Seed demo club'}
        </button>
        <button
          type="button"
          onClick={clearDemoData}
          disabled={busy || !uid}
          className="inline-flex items-center gap-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-700 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          Clear demo data
        </button>
      </div>

      {log.length > 0 && (
        <pre className="mt-4 whitespace-pre-wrap rounded-md bg-zinc-900 p-4 text-xs text-zinc-100">
          {log.join('\n')}
        </pre>
      )}
    </div>
  );
}
